import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { MdClose, MdDownload, MdUpload, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useUser } from '../../../contexts/userContext';
import { useColumns } from '../../../contexts/columnsContext';
import { useCard } from '../../../contexts/cardContext';
import { useWhatsappZapi } from '../../../contexts/whatsappZapiContext';
import api from '../../../services/api';
import apiUploads from '../../../services/apiUploads';
import VendaPerdida from '../../VendaPerdida';
import NewSidebar from './components/NewSidebar';
import NewTopHeader from './components/NewTopHeader';
import NewKanbanBoard from './components/NewKanbanBoard';
import NewHomeOverlays from './components/NewHomeOverlays';
import NewAvatar from './components/NewAvatar';
import NewImportExcelModal from './components/NewImportExcelModal';
import { ATTACHMENT_CATEGORIES, HOME_NEW_STORAGE, formatCurrency, getDaysSinceUpdate } from './utils';
import './style.css';

const LOCAL_SEARCH_TYPES = [
  ['name', 'Nome'], ['produto', 'Produto'], ['fone', 'Telefone'], ['valor', 'Valor'],
  ['orcamento', 'Orçamento'], ['pedido', 'Pedido'], ['city', 'Cidade'],
  ['column', 'ID da coluna'], ['origem', 'Origem'], ['responsavel', 'Responsável'],
  ['tipo_cliente', 'Tipo de cliente'], ['motivo_perda', 'Motivo de perda'],
];

const GLOBAL_SEARCH_TYPES = [
  ['name', 'Nome'], ['nome_obra', 'Nome da obra'],
  ['document_number', 'Número do documento'], ['pedido_number', 'Número do pedido'],
];

const ORDER_OPTIONS = [
  ['dataCreate', 'Data de criação'], ['dataUpdate', 'Data de atualização'],
  ['dataStatus', 'Data de status'], ['nome', 'Nome'], ['value', 'Valor'], ['task', 'Tarefa'],
];

function HomeNewPage() {
  const navigate = useNavigate();
  const { logout } = useContext(GlobalContext);
  const { user, listAllUsers, afilhadosList, getAccessLevel, clearUserContext, empresa, updateUser } = useUser();
  const {
    columns,
    columnsUser,
    editableColumns,
    filteredColumnsBuscaLocal,
    boards,
    setores,
    selectedSetores,
    setSelectedSetores,
    handleSetorChange,
    selectedAfilhados,
    setSelectedAfilhados,
    showSharedCards,
    setShowSharedCards,
    dataInicial,
    setDataInicial,
    dataFinal,
    setDataFinal,
    dataType,
    setDataType,
    orderBy,
    setOrderBy,
    isAscending,
    setIsAscending,
    handleLocalSearch,
    localSearchTerm,
    selectedEtiqueta,
    setSelectedEtiqueta,
    listaEtiquetas,
    selectedAnexoSetor,
    setSelectedAnexoSetor,
    selectedTipoCliente,
    setSelectedTipoCliente,
    listaTiposCliente,
    tipoBuscaLocal,
    setTipoBuscaLocal,
    nivelBuscaCards,
    setNivelBuscaCards,
    buscaCombinadaComColuna,
    setBuscaCombinadaComColuna,
    setLoadingModal,
    setLoadingResult,
    setShowFaktoryModule,
  } = useColumns();
  const {
    cards,
    setCards,
    columnCards,
    columnSummaries,
    kanbanReady,
    loadMoreColumnCards,
    loadKanbanData,
    selectedTemperatures,
    setSelectedTemperatures,
    toggleTemperatureSelection,
    addHistoricoCardContext,
    fetchAllFilteredCardsForExport,
    searchTerm,
    setSearchTerm,
    previewSearchCards,
    setPreviewSearchCards,
    setCurrentCardData,
    setOpenCloseUpdateCard,
    setOpenCloseHistoricModal,
    setOpenCloseTarefasModal,
    setOpenCloseCompartilharModal,
    setOpenCloseModuloEsquadriasModal,
    setOpenCloseAnexosModal,
    setOpenClosePedidosModal,
    setOpenCloseEmailModal,
    setOpenCloseDocumentosRelatoriosModal,
    setListNotifications,
    listNotifications,
    setCurrentCardIdMessage,
    setOpenCloseModalMessenger,
  } = useCard();
  const { openWhatsappZapi, getCardContacts } = useWhatsappZapi();

  const [theme, setTheme] = useState(() => user?.home_new_theme === 'dark' ? 'dark' : 'light');
  const [density, setDensity] = useState(() => localStorage.getItem(HOME_NEW_STORAGE.density) || 'comfortable');
  const [valuesVisible, setValuesVisible] = useState(() => user?.home_new_show_values === true);
  const [themeSaving, setThemeSaving] = useState(false);
  const [valuesPreferenceSaving, setValuesPreferenceSaving] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [panel, setPanel] = useState(null);
  const [searchScope, setSearchScope] = useState('board');
  const [searchField, setSearchField] = useState('name');
  const [peopleQuery, setPeopleQuery] = useState('');
  const [peopleSelectionCleared, setPeopleSelectionCleared] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [lostCard, setLostCard] = useState(null);
  const [notice, setNotice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [tempDates, setTempDates] = useState({ start: dataInicial || '', end: dataFinal || '', type: dataType || 'created_at' });

  const visibleAfilhados = useMemo(() => (
    [5, 8].includes(Number(user?.access_level))
      ? (listAllUsers || []).filter((item) => Number(item.id) !== Number(user?.id))
      : (afilhadosList || [])
  ), [afilhadosList, listAllUsers, user?.access_level, user?.id]);

  const filteredPeople = useMemo(() => {
    const query = peopleQuery.trim().toLocaleLowerCase('pt-BR');
    return [user, ...visibleAfilhados]
      .filter(Boolean)
      .filter((item) => !query || `${item.username || ''} ${item.state || ''} ${item.user_type || ''}`.toLocaleLowerCase('pt-BR').includes(query))
      .sort((a, b) => String(a.state || '').localeCompare(String(b.state || '')) || String(a.username || '').localeCompare(String(b.username || '')));
  }, [peopleQuery, user, visibleAfilhados]);
  const allPeopleIds = useMemo(() => [user, ...visibleAfilhados].filter(Boolean).map((item) => item.id), [user, visibleAfilhados]);
  const allPeopleSelected = allPeopleIds.length > 0 && allPeopleIds.every((id) => selectedAfilhados.some((selectedId) => Number(selectedId) === Number(id)));

  useEffect(() => {
    if (!boards.length) return;
    if (!boards.some((board) => String(board.id) === String(selectedBoardId))) setSelectedBoardId(String(boards[0].id));
  }, [boards, selectedBoardId]);

  useEffect(() => {
    setTheme(user?.home_new_theme === 'dark' ? 'dark' : 'light');
    setValuesVisible(user?.home_new_show_values === true);
  }, [user?.home_new_show_values, user?.home_new_theme, user?.id]);

  useEffect(() => {
    if (!user?.id || selectedAfilhados.length || peopleSelectionCleared) return;
    setSelectedAfilhados([user.id, ...visibleAfilhados.map((item) => item.id)]);
  }, [peopleSelectionCleared, setSelectedAfilhados, selectedAfilhados.length, user?.id, visibleAfilhados]);

  useEffect(() => setTempDates({ start: dataInicial || '', end: dataFinal || '', type: dataType || 'created_at' }), [dataInicial, dataFinal, dataType]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = setTimeout(() => setNotice(null), 3200);
    return () => clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    api.get(`/card/tasks/overdue/${user.id}`)
      .then((response) => { if (active) setListNotifications(Array.isArray(response.data) ? response.data : []); })
      .catch(() => { if (active) setListNotifications([]); });
    return () => { active = false; };
  }, [setListNotifications, user?.id]);

  const visibleColumns = useMemo(() => columnsUser.filter((column) => (
    (!selectedBoardId || Number(column.board_id) === Number(selectedBoardId))
    && selectedSetores.includes(column.setor)
    && (!filteredColumnsBuscaLocal.length || filteredColumnsBuscaLocal.some((item) => Number(item.id) === Number(column.id)))
  )), [columnsUser, filteredColumnsBuscaLocal, selectedBoardId, selectedSetores]);

  const getColumnName = useCallback((id) => columnsUser.find((item) => Number(item.id) === Number(id))?.name || 'Etapa não encontrada', [columnsUser]);

  const patchCard = useCallback((updated) => {
    setCards((current) => current.map((item) => Number(item.card_id) === Number(updated.card_id) ? { ...item, ...updated } : item));
    setPreviewSearchCards((current) => current.map((item) => Number(item.card_id) === Number(updated.card_id) ? { ...item, ...updated } : item));
    setListNotifications((current) => current.map((item) => Number(item.card_id) === Number(updated.card_id) ? { ...item, ...updated } : item));
  }, [setCards, setListNotifications, setPreviewSearchCards]);

  const clearCardLabel = useCallback(async (card) => {
    const response = await api.post('/card/update-etiqueta', { cardId: card.card_id, etiqueta_id: null });
    patchCard({ ...response.data, card_id: card.card_id });
  }, [patchCard]);

  const handleDragEnd = useCallback(async ({ destination, source, draggableId }) => {
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;
    const { cardData, block_column: blocked } = JSON.parse(draggableId);
    const from = Number(source.droppableId);
    const to = Number(destination.droppableId);
    const hasLocation = (cardData.city && cardData.state) || cardData.cidade_estado_internacional;
    if (!hasLocation || !cardData.origem || cardData.origem === 'Sem Origem' || !String(cardData.fone || '').trim()) {
      const field = !hasLocation ? 'cidade/estado' : (!cardData.origem || cardData.origem === 'Sem Origem') ? 'origem' : 'telefone';
      alert(`Não é possível mover o card. Campo ${field} não preenchido.`); return;
    }
    if (!cardData.tipo_cliente_id) { alert('Não é possível mover o card. Campo Tipo de Cliente não preenchido.'); return; }
    if (!getAccessLevel('coluna')) { alert('Não autorizado pelo Administrador!'); return; }
    if (blocked && !['Em Contato', 'Sem Retorno'].includes(getColumnName(to))) {
      alert("O card está bloqueado e só pode ser movido para 'Em Contato' ou 'Sem Retorno'."); return;
    }
    const editable = editableColumns.map((item) => Number(item.columnId));
    if (!editable.includes(from)) { alert('Você não tem permissão para mover o card a partir desta coluna.'); return; }
    if (!editable.includes(to)) { alert('Você não tem permissão para mover o card para esta coluna.'); return; }
    if (!window.confirm('Você tem certeza que deseja alterar?')) return;

    const before = cards;
    patchCard({ ...cardData, column_id: to });
    setLoadingModal(true); setLoadingResult('Alterando coluna…');
    try {
      const response = await api.post('/card/update-column', { cardId: cardData.card_id, columnId: to });
      patchCard({ ...cardData, ...response.data, column_id: to });
      await Promise.all([
        addHistoricoCardContext(`Coluna alterada para ${getColumnName(to)}`, cardData.card_id, user.id),
        clearCardLabel(cardData),
      ]);
      setNotice({ tone: 'success', text: `Card movido para ${getColumnName(to)}` });
    } catch (error) {
      setCards(before);
      setNotice({ tone: 'danger', text: 'Não foi possível mover o card.' });
    } finally { setLoadingModal(false); setLoadingResult(''); }
  }, [addHistoricoCardContext, cards, clearCardLabel, editableColumns, getAccessLevel, getColumnName, patchCard, setCards, setLoadingModal, setLoadingResult, user?.id]);

  const openCardFlow = useCallback((kind, card) => {
    setCurrentCardData(card);
    const flows = {
      edit: setOpenCloseUpdateCard,
      history: setOpenCloseHistoricModal,
      tasks: setOpenCloseTarefasModal,
      share: setOpenCloseCompartilharModal,
      production: setOpenCloseModuloEsquadriasModal,
      attachments: setOpenCloseAnexosModal,
      orders: setOpenClosePedidosModal,
      email: setOpenCloseEmailModal,
      documents: setOpenCloseDocumentosRelatoriosModal,
    };
    if (kind === 'attachments' && !user?.is_premium) { alert('Você não é Premium'); return; }
    if (kind === 'orders' && card.status !== 'Vendido') { alert("Pedidos só podem ser abertos para cards com status 'Vendido'."); return; }
    flows[kind]?.(true);
  }, [setCurrentCardData, setOpenCloseAnexosModal, setOpenCloseCompartilharModal, setOpenCloseDocumentosRelatoriosModal, setOpenCloseEmailModal, setOpenCloseHistoricModal, setOpenCloseModuloEsquadriasModal, setOpenClosePedidosModal, setOpenCloseTarefasModal, setOpenCloseUpdateCard, user?.is_premium]);

  const loadCardDetails = useCallback(async (card) => {
    try {
      const response = await api.get(`/card/find-by-id/${card.card_id}`);
      const detailedCard = { ...card, ...response.data, card_id: card.card_id };
      patchCard(detailedCard);
      return detailedCard;
    } catch (error) {
      setNotice({ tone: 'danger', text: 'Não foi possível atualizar os detalhes do card.' });
      throw error;
    }
  }, [patchCard]);

  const rateCard = useCallback(async (card, value) => {
    if (!getAccessLevel('estrelas')) { alert('Não autorizado pelo Administrador!'); return; }
    if (!window.confirm('Você tem certeza que deseja alterar?')) return;
    try {
      const response = await api.post('/card/update-potencial-venda', { id: card.card_id, potencial_venda: value });
      patchCard({ ...response.data, card_id: card.card_id, potencial_venda: value });
      addHistoricoCardContext(`Potencial de Venda alterado para ${value} estrelas`, card.card_id, user.id);
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível alterar o potencial.' }); }
  }, [addHistoricoCardContext, getAccessLevel, patchCard, user?.id]);

  const labelCard = useCallback(async (card, labelId) => {
    if (!getAccessLevel('etiqueta')) { alert('Não autorizado pelo Administrador!'); return; }
    if (!window.confirm('Você tem certeza que deseja alterar?')) return;
    try {
      const response = await api.post('/card/update-etiqueta', { cardId: card.card_id, etiqueta_id: labelId });
      patchCard({ ...response.data, card_id: card.card_id, etiqueta_id: labelId });
      addHistoricoCardContext('Etiqueta alterada', card.card_id, user.id);
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível alterar a etiqueta.' }); }
  }, [addHistoricoCardContext, getAccessLevel, patchCard, user?.id]);

  const statusCard = useCallback(async (card, status) => {
    if (!getAccessLevel('status')) { alert('Não autorizado pelo Administrador!'); return; }
    if (status === 'Perdido') { setCurrentCardData(card); setLostCard(card); return; }
    if (!window.confirm('Você tem certeza que deseja alterar?')) return;
    const soldColumn = columns.find((column) => column.name === empresa?.coluna_vendido);
    try {
      const response = await api.post('/card/update-status', { id: card.card_id, status, columnId: soldColumn && status ? soldColumn.id : card.column_id });
      patchCard({ ...response.data, card_id: card.card_id, status });
      addHistoricoCardContext(`Status alterado de ${card.status || 'Sem Status'} para ${status || 'Sem Status'}`, card.card_id, user.id);
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível alterar o status.' }); }
  }, [addHistoricoCardContext, columns, empresa?.coluna_vendido, getAccessLevel, patchCard, setCurrentCardData, user?.id]);

  const lockCard = useCallback(async (card, value) => {
    if (![5, 8, 9].includes(Number(user?.access_level))) { alert('Apenas Administrador pode realizar essa ação!'); return; }
    if (!window.confirm('Você tem certeza que deseja alterar?')) return;
    try {
      const response = await api.post('/card/update-block-column', { id: card.card_id, block_column: value });
      patchCard({ ...response.data, card_id: card.card_id, block_column: value });
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível alterar o bloqueio.' }); }
  }, [patchCard, user?.access_level]);

  const openWhatsapp = useCallback(async (card) => {
    try {
      const contacts = await getCardContacts(card.card_id);
      if (!contacts?.length) { alert('Este card ainda não tem contato vinculado.'); return; }
      const contact = contacts.find((item) => item.is_primary) || contacts[0];
      await openWhatsappZapi({ ...contact, contact_id: contact.contact_id || contact.id });
    } catch (error) { alert(error.response?.data?.message || 'Não foi possível abrir a conversa WhatsApp.'); }
  }, [getCardContacts, openWhatsappZapi]);

  const openDirectWhatsapp = useCallback((card) => {
    const phone = String(card.fone || '').trim();
    if (!phone) { alert('Telefone não informado.'); return; }
    if (!window.confirm('Iniciar conversa pelo WhatsApp?')) return;

    const cleanPhone = phone.replace(/[^\d]/g, '');
    const countryCode = card.pais === 'Paraguai' ? '+595' : '+55';
    const message = encodeURIComponent(`Olá ${card.name || ''}, tudo bem?\nMeu nome é ${user?.username || ''}, recebi sua solicitação de orçamento pela equipe de marketing da Bazze PVC.\n\nPor gentileza, teria alguma observação para acrescentar a sua solicitação de orçamento?\n\nFico a disposição e aguardo seu retorno!`);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const url = isMobile
      ? `https://api.whatsapp.com/send?phone=${countryCode}${cleanPhone}&text=${message}`
      : `https://web.whatsapp.com/send?phone=${countryCode}${cleanPhone}&text=${message}`;

    if (isMobile) window.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
    addHistoricoCardContext(`Conversa iniciada pelo WhatsApp ${phone}`, card.card_id, user?.id);
  }, [addHistoricoCardContext, user?.id, user?.username]);

  const notifyPartner = useCallback(async (card) => {
    const canNotify = [5, 8, 9].includes(Number(user?.access_level)) || user?.user_type === 'Supervisor Comercial';
    if (!canNotify) { alert('Não autorizado pelo Administrador!'); return; }

    const entity = [user, ...(listAllUsers || [])].filter(Boolean).find((item) => Number(item.id) === Number(card.entity_id));
    const entityPhone = String(entity?.fone || '').trim();
    if (!entityPhone) { alert('Telefone do parceiro não informado.'); return; }
    if (!window.confirm('Enviar mensagem para Parceiro?')) return;

    const message = `${entity?.username || 'Parceiro'}\n \nCard com Link Patrocinado criado com sucesso no CRM! \n \nCliente: ${card.name || ''} \nNúmero do orçamento: ${card.document_number || ''} \nValor: ${card.sale_value ? formatCurrency(card.sale_value) : '0,00'}\n\n Verifique as informações fornecidas!\n\nBazze PVC`;
    try {
      await api.post('/card/enviarMensagemParaBotConversa', null, {
        params: { empresaId: user?.empresa_id, numero: entityPhone, contato: '', mensagem: message },
      });
      alert('Mensagem enviada!');
    } catch {
      alert('Não foi possível enviar a mensagem para o parceiro.');
    }
  }, [listAllUsers, user]);

  const notifyOwner = useCallback(async (card) => {
    const entity = [user, ...(listAllUsers || [])].filter(Boolean).find((item) => Number(item.id) === Number(card.entity_id));
    if (!entity?.email) { alert('Não foi possível encontrar o email da entidade responsável.'); return; }
    if (!window.confirm(`Enviar um e-mail de aviso para ${entity.username || 'o responsável'} solicitando atenção ao card?`)) return;

    const emailData = {
      to: entity.email,
      subject: `Atenção necessária - Card ${card.name || 'Sem Nome'}`,
      text: `Olá ${entity.username || 'Responsável'},\n\nEste é um aviso automático do sistema CRM para informar que o card do cliente "${card.name || 'Sem Nome'}" precisa de sua atenção.\n\nDetalhes do Card:\n- Cliente: ${card.name || 'Não informado'}\n- Número do Documento: ${card.document_number || 'Não informado'}\n- Cidade/Estado: ${card.city || 'Não informado'}/${card.state || 'Não informado'}\n- Valor de Venda: ${card.sale_value ? formatCurrency(card.sale_value) : 'R$ 0,00'}\n- Coluna: ${getColumnName(card.column_id)}\n- Status: ${card.status || 'Não informado'}\n- Dias sem atualização: ${getDaysSinceUpdate(card.updated_at)}\n\nPor favor, acesse o CRM e verifique este card para tomar as ações necessárias.\n\nEste aviso foi enviado por ${user?.username || 'SyncsCRM'} (${user?.email || ''}) em ${new Date().toLocaleString('pt-BR')}.\n\nAtenciosamente,\nSistema CRM`,
    };

    setLoadingModal(true); setLoadingResult('Enviando e-mail…');
    try {
      await api.post(`/users/send-email/${user?.empresa_id}`, emailData);
      alert(`Email enviado com sucesso para ${entity.username || 'o responsável'} (${entity.email})!`);
      addHistoricoCardContext(`Email de aviso enviado para ${entity.username || 'o responsável'} solicitando atenção ao card`, card.card_id, user?.id);
    } catch {
      alert('Erro ao enviar o e-mail. Tente novamente.');
    } finally {
      setLoadingModal(false); setLoadingResult('');
    }
  }, [addHistoricoCardContext, getColumnName, listAllUsers, setLoadingModal, setLoadingResult, user]);

  const openInternalChat = useCallback((card) => {
    setCurrentCardData(card);
    setCurrentCardIdMessage(card.card_id);
    setOpenCloseModalMessenger(true);
  }, [setCurrentCardData, setCurrentCardIdMessage, setOpenCloseModalMessenger]);

  const copyTrackingLink = useCallback(async (card) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/tracking/${card.card_id}`);
      setNotice({ tone: 'success', text: 'Link de acompanhamento copiado.' });
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível copiar o link.' }); }
  }, []);

  const openOriginalCard = useCallback(async (card) => {
    try {
      const response = await api.get(`/card/find-by-id/${card.card_original_id}`);
      setCurrentCardData(response.data);
      setOpenCloseUpdateCard(true);
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível carregar o card original.' }); }
  }, [setCurrentCardData, setOpenCloseUpdateCard]);

  const openFaktory = useCallback((card) => {
    if (![5, 8, 9].includes(Number(user?.access_level))) { alert('Você não tem permissão para acessar este módulo.'); return; }
    if (!card.pedido_number || Number.isNaN(Number(card.pedido_number))) { alert('O número do pedido é inválido.'); return; }
    setCurrentCardData(card);
    setShowFaktoryModule(true);
  }, [setCurrentCardData, setShowFaktoryModule, user?.access_level]);

  const updateTemperature = useCallback(async (card, value) => {
    if (![5, 8, 9].includes(Number(user?.access_level))) { alert('Apenas administradores podem alterar a temperatura.'); return; }
    try {
      await api.put(`/card/temperatura-card/${card.card_id}`, { temperatura_card: value });
      if (Number(card.temperatura_card) !== 0) await api.put(`/card/update-valor-lead/${card.card_id}`, { temperatura_card: value });
      patchCard({ ...card, temperatura_card: value });
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível alterar a temperatura.' }); }
  }, [patchCard, user?.access_level]);

  const createProductionStage = useCallback(async (card) => {
    if (!getAccessLevel('etapaProducao')) { alert('Não autorizado pelo ADM!'); return; }
    if (!window.confirm('Criar etapa de Produção?')) return;
    setLoadingModal(true); setLoadingResult('Criando etapa de produção…');
    try {
      const response = await api.post('/card/import-suiteflow', {
        created_at: new Date(), name: card.name || '', document_number: card.document_number || '', cost_value: 0,
        column_id: card.column_id, entity_id: card.entity_id, empresa_id: user.empresa_id, origem: card.origem || '',
        sale_value: 0, potencial_venda: card.potencial_venda, produto: card.produto, status: card.status,
        motivo_venda_perdida: card.motivo_venda_perdida, nivel_prioridade: card.nivel_prioridade,
        status_date: card.status_date, updated_at: card.updated_at, email: card.email || '', fone: card.fone || '',
        state: card.state || '', city: card.city || '', pedido_number: card.pedido_number || '',
        etapa_producao: Number(card.etapa_producao || 0) + 1, etiqueta_id: card.etiqueta_id || 1, card_original_id: card.card_id,
      });
      const created = response.data;
      await api.post('/card/copy-compartilhamentos', { card_original_id: card.card_id, novo_card_id: created.card_id }).catch(() => undefined);
      setCards((current) => [...current, created]);
      addHistoricoCardContext(`Etapa de Produção ${Number(card.etapa_producao || 0) + 1} criada com sucesso`, card.card_id, user.id);
      setNotice({ tone: 'success', text: 'Etapa de produção criada.' });
    } catch (error) { setNotice({ tone: 'danger', text: error.response?.data?.message || 'Não foi possível criar a etapa.' }); }
    finally { setLoadingModal(false); setLoadingResult(''); }
  }, [addHistoricoCardContext, getAccessLevel, setCards, setLoadingModal, setLoadingResult, user?.empresa_id, user?.id]);

  const removeCard = useCallback(async (card) => {
    if (!getAccessLevel('excluir')) { alert('Não autorizado pelo ADM!'); return; }
    if (!window.confirm('Excluir Card?')) return;
    try {
      const anexos = (await api.get(`/card/${card.card_id}/anexos`)).data || [];
      await api.delete(`/card/${card.card_id}`);
      await Promise.allSettled(anexos.map((anexo) => apiUploads.delete(`/uploads/${anexo.nome_arquivo}`, { params: { empresaId: user.empresa_id, cardId: card.card_id, userId: user.id, clientName: card.name } })));
      setCards((current) => current.filter((item) => Number(item.card_id) !== Number(card.card_id)));
      setNotice({ tone: 'success', text: 'Card excluído com sucesso.' });
    } catch (error) { setNotice({ tone: 'danger', text: error.response?.data?.message || 'Não foi possível excluir o card.' }); }
  }, [getAccessLevel, setCards, user?.empresa_id, user?.id]);

  const moveCard = useCallback((card, columnId) => handleDragEnd({
    destination: { droppableId: String(columnId), index: 0 },
    source: { droppableId: String(card.column_id), index: 0 },
    draggableId: JSON.stringify({ cardData: card, block_column: card.block_column }),
  }), [handleDragEnd]);

  const getOwner = useCallback((id) => [user, ...(listAllUsers || [])].find((item) => Number(item?.id) === Number(id)), [listAllUsers, user]);
  const editableColumnIds = useMemo(() => editableColumns.map((item) => Number(item.columnId)), [editableColumns]);
  const canAccessValues = getAccessLevel('valor');
  const showValues = canAccessValues && valuesVisible;
  const cardActions = useMemo(() => ({
    open: openCardFlow, rate: rateCard, label: labelCard, status: statusCard, lock: lockCard,
    whatsapp: openWhatsapp, directWhatsapp: openDirectWhatsapp, notifyPartner, notifyOwner,
    chat: openInternalChat, tracking: copyTrackingLink, original: openOriginalCard,
    faktory: openFaktory, temperature: updateTemperature, productionStage: createProductionStage,
    remove: removeCard, move: moveCard, getOwner, getColumnName, loadDetails: loadCardDetails,
    labels: [...listaEtiquetas].sort((a, b) => a.order - b.order),
    columns: columnsUser.filter((column) => editableColumnIds.includes(Number(column.id))),
    editableColumnIds, canViewValue: canAccessValues, showValues, canRate: getAccessLevel('estrelas'), canLabel: getAccessLevel('etiqueta'),
    canEdit: getAccessLevel('editar'), canHistory: getAccessLevel('historico'), canTasks: getAccessLevel('tarefas'),
    canShare: getAccessLevel('compartilhar'), canContact: getAccessLevel('contato'), canAttachments: getAccessLevel('anexos'),
    canProduction: getAccessLevel('producao'), canOrders: getAccessLevel('formularioPedidos'), canStatus: getAccessLevel('status'),
    canProductionStage: getAccessLevel('etapaProducao'), canFaktory: getAccessLevel('faktory'),
    canResetStatus: [5, 8, 9].includes(Number(user?.access_level)), canTemperature: [5, 8, 9].includes(Number(user?.access_level)), canLock: [5, 8, 9].includes(Number(user?.access_level)), canDelete: getAccessLevel('excluir'),
    canNotifyPartner: [5, 8, 9].includes(Number(user?.access_level)) || user?.user_type === 'Supervisor Comercial',
  }), [canAccessValues, columnsUser, copyTrackingLink, createProductionStage, editableColumnIds, getAccessLevel, getColumnName, getOwner, labelCard, listaEtiquetas, loadCardDetails, lockCard, moveCard, notifyOwner, notifyPartner, openCardFlow, openDirectWhatsapp, openFaktory, openInternalChat, openOriginalCard, openWhatsapp, rateCard, removeCard, showValues, statusCard, updateTemperature, user?.access_level, user?.user_type]);

  const runSearch = async () => {
    if (searchScope === 'board') {
      setTipoBuscaLocal(searchField);
      handleLocalSearch(searchTerm.trim());
      setPanel(null);
      return;
    }
    if (searchTerm.trim().length < 2) return;
    setSearching(true); setPreviewSearchCards([]);
    try {
      const response = await api.get('/card/search', { params: { searchType: searchField, searchTerm, entityId: user.id, empresaId: user.empresa_id } });
      setPreviewSearchCards(Array.isArray(response.data) ? response.data : []);
      setPanel('search');
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível concluir a pesquisa.' }); }
    finally { setSearching(false); }
  };

  const reload = () => {
    setSelectedTemperatures([]); setTipoBuscaLocal('name'); setNivelBuscaCards(0); setSelectedEtiqueta(null); setSelectedAnexoSetor(null); setSelectedTipoCliente(null); setSearchTerm(''); setSearchField('name'); handleLocalSearch('');
    loadKanbanData(columnsUser);
  };

  const exportCards = async () => {
    if (!getAccessLevel('exportar') || exporting) return;
    setExporting(true);
    try {
      const data = await fetchAllFilteredCardsForExport();
      const workbook = new ExcelJS.Workbook(); const sheet = workbook.addWorksheet('Cards Filtrados');
      sheet.columns = [
        { header: 'ID', key: 'card_id', width: 12 }, { header: 'Cliente', key: 'name', width: 35 },
        { header: 'Etapa', key: 'column_name', width: 25 }, { header: 'Telefone', key: 'fone', width: 20 },
        { header: 'E-mail', key: 'email', width: 30 }, { header: 'Cidade', key: 'city', width: 22 },
        { header: 'Estado', key: 'state', width: 10 }, { header: 'Origem', key: 'origem', width: 22 },
        { header: 'Produto', key: 'produto', width: 28 }, { header: 'Custo', key: 'cost_value', width: 16 },
        { header: 'Venda', key: 'sale_value', width: 16 }, { header: 'Status', key: 'status', width: 16 },
      ];
      data.forEach((card) => sheet.addRow({ ...card, column_name: getColumnName(card.column_id) }));
      const buffer = await workbook.xlsx.writeBuffer();
      const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a'); link.href = url; link.download = 'cards-filtrados.xlsx'; link.click(); URL.revokeObjectURL(url);
    } catch { setNotice({ tone: 'danger', text: 'Não foi possível exportar os cards.' }); }
    finally { setExporting(false); }
  };

  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : density === 'compact' ? 'expanded' : 'comfortable';
    setDensity(next); localStorage.setItem(HOME_NEW_STORAGE.density, next);
  };
  const toggleTheme = async () => {
    if (!user?.id || themeSaving) return;
    const previous = theme;
    const next = theme === 'dark' ? 'light' : 'dark';

    setThemeSaving(true);
    setTheme(next);
    updateUser({ home_new_theme: next });
    try {
      await api.put(`/users/${user.id}`, { home_new_theme: next });
    } catch {
      setTheme(previous);
      updateUser({ home_new_theme: previous });
      setNotice({ tone: 'danger', text: 'Não foi possível salvar a preferência de tema.' });
    } finally {
      setThemeSaving(false);
    }
  };
  const toggleValuesVisibility = async () => {
    if (!user?.id || valuesPreferenceSaving) return;
    const previous = valuesVisible;
    const next = !valuesVisible;

    setValuesPreferenceSaving(true);
    setValuesVisible(next);
    updateUser({ home_new_show_values: next });
    try {
      await api.put(`/users/${user.id}`, { home_new_show_values: next });
    } catch {
      setValuesVisible(previous);
      updateUser({ home_new_show_values: previous });
      setNotice({ tone: 'danger', text: 'Não foi possível salvar a preferência de exibição dos valores.' });
    } finally {
      setValuesPreferenceSaving(false);
    }
  };
  const handleLogout = () => { clearUserContext(); logout(); navigate('/'); };
  const activeFilterCount = [selectedEtiqueta, selectedAnexoSetor, selectedTipoCliente, selectedTemperatures.length, nivelBuscaCards, localSearchTerm, tipoBuscaLocal === 'vendidos' || tipoBuscaLocal === 'perdidos'].filter(Boolean).length;

  return (
    <div className="new-home" data-theme={theme} data-density={density}>
      <NewSidebar
        user={user}
        getAccessLevel={getAccessLevel}
        theme={theme}
        onThemeChange={toggleTheme}
        themeSaving={themeSaving}
        onLogout={handleLogout}
        showValues={showValues}
        mobileOpen={mobileNavigationOpen}
        onMobileClose={() => setMobileNavigationOpen(false)}
      />
      <div className="new-home-workspace">
        <NewTopHeader
          boards={boards} selectedBoardId={selectedBoardId} onBoardChange={setSelectedBoardId}
          searchValue={searchTerm} onSearchChange={setSearchTerm} onSearch={runSearch}
          searchScope={searchScope} onSearchScopeChange={(scope) => { setSearchScope(scope); setSearchField('name'); }}
          searchType={searchField} onSearchTypeChange={setSearchField}
          searchTypes={searchScope === 'board' ? LOCAL_SEARCH_TYPES : GLOBAL_SEARCH_TYPES}
          onFilters={() => setPanel(panel === 'filters' ? null : 'filters')} onPeople={() => setPanel(panel === 'people' ? null : 'people')}
          onDates={() => setPanel(panel === 'dates' ? null : 'dates')} onNotifications={() => setPanel(panel === 'notifications' ? null : 'notifications')}
          notificationCount={listNotifications.length} onCalendar={() => setShowCalendar(true)} onReload={reload}
          density={density} onDensityChange={cycleDensity} activeFilterCount={activeFilterCount}
          onOpenNavigation={() => setMobileNavigationOpen(true)}
        />

        <div className="new-home-subbar">
          <div className="new-home-pipeline-stats">
            <span><b>{visibleColumns.length}</b> etapas</span>
            <span><b>{columnSummaries.reduce((sum, item) => sum + Number(item.count || 0), 0)}</b> oportunidades</span>
            {canAccessValues && <span><b>{showValues ? formatCurrency(columnSummaries.reduce((sum, item) => sum + Number(item.total_value || 0), 0)) : '••••••'}</b> em pipeline</span>}
          </div>
          {(canAccessValues || getAccessLevel('adm') || getAccessLevel('exportar')) && (
            <div className="new-home-subbar-actions">
              {canAccessValues && (
                <button
                  className={`new-home-subbar-value-toggle ${valuesVisible ? 'is-active' : ''}`}
                  type="button"
                  onClick={toggleValuesVisibility}
                  disabled={valuesPreferenceSaving}
                  title={valuesPreferenceSaving ? 'Salvando preferência…' : valuesVisible ? 'Ocultar valores' : 'Mostrar valores'}
                  aria-label={valuesVisible ? 'Ocultar valores financeiros' : 'Mostrar valores financeiros'}
                  aria-pressed={valuesVisible}
                >
                  {valuesVisible ? <MdVisibility /> : <MdVisibilityOff />}
                </button>
              )}
              {getAccessLevel('adm') && <button className="new-home-export-button" onClick={() => setShowImportExcel(true)}><MdUpload />Importar</button>}
              {getAccessLevel('exportar') && <button className="new-home-export-button" onClick={exportCards} disabled={exporting}><MdDownload />{exporting ? 'Exportando…' : 'Exportar'}</button>}
            </div>
          )}
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <NewKanbanBoard columns={visibleColumns} columnCards={columnCards} columnSummaries={columnSummaries} kanbanReady={kanbanReady} loadMoreColumnCards={loadMoreColumnCards} canViewValue={canAccessValues} showValues={showValues} cardActions={cardActions} density={density} />
        </DragDropContext>
      </div>

      {panel && <button className="new-home-panel-scrim" aria-label="Fechar painel" onClick={() => setPanel(null)} />}
      {panel && (
        <aside className="new-home-panel" aria-label="Painel de controles">
          <header><div><span>SyncsCRM</span><h2>{panel === 'filters' ? 'Filtros do quadro' : panel === 'people' ? 'Equipe' : panel === 'dates' ? 'Período' : panel === 'search' ? 'Resultados da busca' : 'Notificações'}</h2></div><button className="new-home-icon-button" onClick={() => setPanel(null)} aria-label="Fechar"><MdClose /></button></header>

          {panel === 'filters' && <div className="new-home-panel-content">
            <label>Ordenação<select value={orderBy || 'dataCreate'} onChange={(event) => setOrderBy(event.target.value)}>{ORDER_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <div className="new-home-segmented"><button className={isAscending ? 'is-active' : ''} onClick={() => setIsAscending(true)}>Crescente</button><button className={!isAscending ? 'is-active' : ''} onClick={() => setIsAscending(false)}>Decrescente</button></div>
            <label>Etiqueta<select value={selectedEtiqueta || ''} onChange={(event) => setSelectedEtiqueta(event.target.value || null)}><option value="">Todas</option>{listaEtiquetas.map((item) => <option key={item.id} value={item.id}>{item.description}</option>)}</select></label>
            <label>Tipo de cliente<select value={selectedTipoCliente || ''} onChange={(event) => setSelectedTipoCliente(event.target.value || null)}><option value="">Todos</option>{listaTiposCliente.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Categoria de anexo<select value={selectedAnexoSetor || ''} onChange={(event) => setSelectedAnexoSetor(event.target.value || null)}><option value="">Todas</option>{ATTACHMENT_CATEGORIES.map((item) => <option key={item}>{item}</option>)}</select></label>
            <fieldset><legend>Temperatura</legend><div className="new-home-chip-row">{[['Frio', 1], ['Morno', 2], ['Quente', 3]].map(([label, value]) => <button key={value} className={selectedTemperatures.includes(value) ? 'is-active' : ''} onClick={() => toggleTemperatureSelection(value)}>{label}</button>)}</div></fieldset>
            <fieldset><legend>Status</legend><div className="new-home-chip-row"><button className={tipoBuscaLocal === 'vendidos' ? 'is-active' : ''} onClick={() => setTipoBuscaLocal(tipoBuscaLocal === 'vendidos' ? 'name' : 'vendidos')}>Vendidos</button><button className={tipoBuscaLocal === 'perdidos' ? 'is-active' : ''} onClick={() => setTipoBuscaLocal(tipoBuscaLocal === 'perdidos' ? 'name' : 'perdidos')}>Perdidos</button></div></fieldset>
            <fieldset><legend>Nível</legend><div className="new-home-chip-row">{[0, 1, 2].map((value) => <button key={value} className={nivelBuscaCards === value ? 'is-active' : ''} onClick={() => setNivelBuscaCards(value)}>{value ? `N${value}` : 'Todos'}</button>)}</div></fieldset>
            <fieldset><legend>Setores</legend><div className="new-home-chip-row"><button onClick={() => setSelectedSetores(setores)}>Selecionar todos</button><button onClick={() => setSelectedSetores([])}>Desmarcar todos</button>{setores.map((item) => <button key={item} className={selectedSetores.includes(item) ? 'is-active' : ''} onClick={() => handleSetorChange(item)}>{item}</button>)}</div></fieldset>
            <label className="new-home-check"><input type="checkbox" checked={buscaCombinadaComColuna} onChange={() => setBuscaCombinadaComColuna(!buscaCombinadaComColuna)} />Ocultar etapas sem resultados</label>
          </div>}

          {panel === 'people' && <div className="new-home-panel-content"><label className="new-home-check"><input type="checkbox" checked={showSharedCards} onChange={() => setShowSharedCards(!showSharedCards)} />Incluir cards compartilhados</label><label>Buscar responsável<input type="search" value={peopleQuery} onChange={(event) => setPeopleQuery(event.target.value)} placeholder="Nome, estado ou função" /></label><div className="new-home-people-list"><button onClick={() => { const nextSelection = allPeopleSelected ? [] : allPeopleIds; setPeopleSelectionCleared(nextSelection.length === 0); setSelectedAfilhados(nextSelection); }}>{allPeopleSelected ? 'Desmarcar todos' : 'Selecionar todos'}</button>{filteredPeople.map((item, index) => <React.Fragment key={item.id}>{(index === 0 || filteredPeople[index - 1]?.state !== item.state) && <div className="new-home-people-state">{item.state || 'Sem estado'}</div>}<label><input type="checkbox" checked={selectedAfilhados.some((id) => Number(id) === Number(item.id))} onChange={() => { const isSelected = selectedAfilhados.some((id) => Number(id) === Number(item.id)); const nextSelection = isSelected ? selectedAfilhados.filter((id) => Number(id) !== Number(item.id)) : [...selectedAfilhados, item.id]; setPeopleSelectionCleared(nextSelection.length === 0); setSelectedAfilhados(nextSelection); }} /><NewAvatar avatar={item.avatar} alt="" /><div><strong>{item.username}</strong><small>{item.user_type}</small></div></label></React.Fragment>)}</div></div>}

          {panel === 'dates' && <div className="new-home-panel-content"><label>Data inicial<input type="date" value={tempDates.start} onChange={(event) => setTempDates({ ...tempDates, start: event.target.value })} /></label><label>Data final<input type="date" value={tempDates.end} onChange={(event) => setTempDates({ ...tempDates, end: event.target.value })} /></label><label>Tipo de data<select value={tempDates.type} onChange={(event) => setTempDates({ ...tempDates, type: event.target.value })}><option value="created_at">Data de criação</option><option value="updated_at">Data de atualização</option></select></label><button className="new-home-primary-button new-home-panel-submit" onClick={() => { setDataInicial(tempDates.start); setDataFinal(tempDates.end); setDataType(tempDates.type); setPanel(null); }}>Aplicar período</button></div>}

          {panel === 'search' && <div className="new-home-panel-content">{searching && <div className="new-home-search-state">Buscando…</div>}{!searching && !previewSearchCards.length && <div className="new-home-search-state">Nenhum resultado encontrado.</div>}<div className="new-home-search-results">{previewSearchCards.map((card) => <button key={card.card_id} onClick={() => openCardFlow('edit', card)}><span>#{card.card_id}</span><strong>{card.name}</strong><small>{card.city || card.origem || 'Sem detalhes'}</small></button>)}</div></div>}

          {panel === 'notifications' && <div className="new-home-panel-content"><div className="new-home-search-results">{listNotifications.length ? listNotifications.map((item) => <button key={item.task_id || `${item.card_id}-${item.due_date}`} onClick={() => openCardFlow('tasks', item)}><span>{item.due_date ? new Date(item.due_date).toLocaleDateString('pt-BR') : 'Pendente'}</span><strong>{item.description || item.name}</strong><small>{item.name}</small></button>) : <div className="new-home-search-state">Nenhuma tarefa pendente.</div>}</div></div>}
        </aside>
      )}

      {lostCard && <VendaPerdida cardData={lostCard} closeModal={() => setLostCard(null)} />}
      {showImportExcel && <NewImportExcelModal onClose={() => setShowImportExcel(false)} onImported={() => loadKanbanData(columnsUser)} />}
      {notice && <div className={`new-home-toast tone-${notice.tone}`} role="status">{notice.text}</div>}
      <NewHomeOverlays theme={theme} showCalendar={showCalendar} onCloseCalendar={() => setShowCalendar(false)} />
    </div>
  );
}

export default HomeNewPage;
